import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { Alert, Button } from '@betareader/design-system';
import Icon from '../../components/AppIcon';
import styled from 'styled-components';
import {
  CritiqueGroup,
  CritiqueGroupVisibility,
  useUpdateGroupVisibility,
} from 'src/hooks/useCritiqueGroups';
import useCanHostCritiqueGroups from './useCanHostCritiqueGroups';
import { TierGateModal } from 'src/components/critiqueGroups/tierGate';

/**
 * Host-only "Who can join?" panel — private ↔ link-only ↔ public.
 *
 * A sibling Panel to the feedback-visibility one, NOT a section inside it:
 * joining and feedback-visibility are different concepts, they save to
 * different endpoints, and the two share nothing but styling.
 *
 * Paid gate: for a FREE host the two public options render disabled with a
 * "Premium" pill; clicking one opens the upgrade modal instead of selecting it.
 * The gate is presentational — the server enforces it, and this ALSO handles
 * UPGRADE_REQUIRED_PUBLIC_GROUP coming back (a plan can lapse mid-session, so
 * `canHost` being true is never a guarantee).
 */

const Panel = styled.div`
  background: var(--br-surface);
  border: 1px solid var(--br-border);
  border-radius: 14px;
  overflow: hidden;
  max-width: 620px;
  margin-bottom: 18px;
`;

const Head = styled.div`
  padding: 18px 24px;
  border-bottom: 1px solid var(--br-border);
  font-weight: 800;
  font-size: 17px;
`;

const Body = styled.div`
  padding: 24px;
`;

const Foot = styled.div`
  padding: 14px 24px;
  border-top: 1px solid var(--br-border);
  background: var(--br-raised-surface);
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 12px;
`;

const Option = styled.button<{ $on: boolean; $locked?: boolean }>`
  box-sizing: border-box;
  width: 100%;
  border: ${({ $on }) => ($on ? '2px solid var(--br-accent)' : '1.5px solid var(--br-border)')};
  background: ${({ $on }) => ($on ? 'var(--br-raised-surface)' : 'var(--br-surface)')};
  color: var(--br-text);
  border-radius: 12px;
  padding: 13px 15px;
  text-align: left;
  font: inherit;
  display: flex;
  align-items: flex-start;
  gap: 10px;
  cursor: pointer;
  opacity: ${({ $locked }) => ($locked ? 0.7 : 1)};
  &:focus-visible { outline: 2px solid var(--br-focus); outline-offset: 2px; }
`;

const Radio = styled.span<{ $on: boolean }>`
  width: 16px;
  height: 16px;
  border-radius: 50%;
  border: 4px solid ${({ $on }) => ($on ? 'var(--br-accent)' : 'var(--br-border)')};
  margin-top: 2px;
  flex-shrink: 0;
`;

const OptionTitle = styled.span`
  font-weight: 700;
  font-size: 13.5px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const OptionHelp = styled.span`
  display: block;
  font-size: 12px;
  color: var(--br-muted);
  margin-top: 4px;
`;

const PremiumBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 2px 7px;
  border: 1px solid var(--br-warning-border);
  border-radius: 999px;
  background: var(--br-warning-surface);
  color: var(--br-warning-text);
  font-size: 11px;
  font-weight: 700;
`;

type Choice = {
  value: CritiqueGroupVisibility;
  title: string;
  help: string;
  paid: boolean;
  testid: string;
};

const VisibilityPanel: React.FC<{ group: CritiqueGroup }> = ({ group }) => {
  const { t } = useTranslation();
  const history = useHistory();
  const { canHost } = useCanHostCritiqueGroups();
  const save = useUpdateGroupVisibility(group._id);

  const [visibility, setVisibility] = useState<CritiqueGroupVisibility>(
    group.visibility
  );
  const [saved, setSaved] = useState(false);
  const [gateOpen, setGateOpen] = useState(false);

  useEffect(() => {
    setVisibility(group.visibility);
  }, [group.visibility]);

  const choices: Choice[] = [
    {
      value: 'invite_only',
      title: t('CritiquePrivate', 'Private'),
      help: t(
        'CritiquePrivateHelp',
        'Only people you add can see or join this group.'
      ),
      paid: false,
      testid: 'visibility-invite-only',
    },
    {
      value: 'request',
      title: t('CritiqueLinkOnly', 'Link-only'),
      help: t(
        'CritiqueLinkOnlyHelp',
        'Anyone with the link can see the group and ask to join. Not listed publicly.'
      ),
      paid: true,
      testid: 'visibility-request',
    },
    {
      value: 'listed',
      title: t('CritiquePublic', 'Public'),
      help: t(
        'CritiquePublicHelp',
        'Listed in the discovery catalog so writers can find your group and ask to join.'
      ),
      paid: true,
      testid: 'visibility-listed',
    },
  ];

  const pick = (choice: Choice) => {
    // A free host who reaches for a paid option gets the upgrade prompt instead
    // of a selection — show the value, lock the payoff.
    if (choice.paid && !canHost) {
      setGateOpen(true);
      return;
    }
    setSaved(false);
    setVisibility(choice.value);
  };

  const dirty = visibility !== group.visibility;

  const submit = () => {
    setSaved(false);
    save.mutate(
      { visibility },
      {
        onSuccess: () => setSaved(true),
        onError: err => {
          // Belt-and-braces: the server is the authority, so honour a late
          // upgrade requirement even if `canHost` said otherwise.
          if (err.code === 'UPGRADE_REQUIRED_PUBLIC_GROUP') {
            setVisibility(group.visibility);
            setGateOpen(true);
          }
        },
      }
    );
  };

  return (
    <>
      <Panel data-testid="visibility-settings">
        <Head>{t('CritiqueWhoCanJoin', 'Who can join?')}</Head>
        <Body>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {choices.map(choice => {
              const on = visibility === choice.value;
              const locked = choice.paid && !canHost;
              return (
                <Option
                  key={choice.value}
                  type="button"
                  aria-pressed={on}
                  $on={on}
                  $locked={locked}
                  onClick={() => pick(choice)}
                  data-testid={choice.testid}
                >
                  <Radio $on={on} aria-hidden="true" />
                  <span style={{ flex: 1 }}>
                    <OptionTitle>
                      {choice.title}
                      {locked && (
                        <PremiumBadge data-testid={`${choice.testid}-premium`}>
                          <Icon name="gem" style={{ margin: '0 4px 0 0' }} />
                          {t('Premium', 'Premium')}
                        </PremiumBadge>
                      )}
                    </OptionTitle>
                    <OptionHelp>{choice.help}</OptionHelp>
                  </span>
                </Option>
              );
            })}
          </div>

          {save.isError &&
            save.error?.code !== 'UPGRADE_REQUIRED_PUBLIC_GROUP' && (
              <Alert variant="error"
                data-testid="visibility-error"
                style={{ marginTop: 14 }}
              >
                {save.error?.message}
              </Alert>
            )}
        </Body>
        <Foot>
          {saved && !dirty && (
            <span role="status" style={{ fontSize: 12.5, color: 'var(--br-success-text)', fontWeight: 700 }}>
              {t('CritiqueSettingsSaved', 'Saved')}
            </span>
          )}
          <Button
            onClick={submit}
            disabled={!dirty || save.isPending}
            aria-busy={save.isPending}
            style={{ borderRadius: 9, fontWeight: 700 }}
            data-testid="visibility-save"
          >
            {t('CritiqueSaveSettings', 'Save settings')}
          </Button>
        </Foot>
      </Panel>

      <TierGateModal
        open={gateOpen}
        onClose={() => setGateOpen(false)}
        onUpgrade={() => history.push('/account/subscription')}
        limit="public"
        serverMessage={
          save.error?.code === 'UPGRADE_REQUIRED_PUBLIC_GROUP'
            ? save.error?.message
            : undefined
        }
      />
    </>
  );
};

export default VisibilityPanel;
